import { Button, Dialog, Skeleton } from '@zyxantech/tux'
import { Download, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useGenerateReportMutation, useGetReportRunsQuery, type ReportRun } from '@/api/reports.api'
import { ErrorState } from '@/components/shared/ErrorState'
import { env } from '@/config/env'
import { ACTION, RESOURCE } from '@/constants/permissions'
import { usePermission } from '@/hooks/usePermission'
import { formatTimestamp } from '@/utils/format'
import { ReportStatusBadge } from './ReportStatusBadge'

const POLL_INTERVAL_MS = 4_000
const ACTIVE_STATUSES = new Set(['pending', 'running'])

interface ReportRunsDrawerProps {
  reportId: string
  reportName: string
  onClose: () => void
}

export function ReportRunsDrawer({ reportId, reportName, onClose }: ReportRunsDrawerProps) {
  const canGenerate = usePermission(RESOURCE.REPORT, ACTION.CREATE)
  const [generate, { isLoading: isGenerating }] = useGenerateReportMutation()
  const [pollEnabled, setPollEnabled] = useState(false)

  const { data, isLoading, error, refetch } = useGetReportRunsQuery(reportId, {
    pollingInterval: pollEnabled ? POLL_INTERVAL_MS : 0,
  })

  // Start polling when there are active runs; stop when all settle.
  useEffect(() => {
    const hasActive = (data?.items ?? []).some((r) => ACTIVE_STATUSES.has(r.status))
    setPollEnabled(hasActive)
  }, [data])

  const handleGenerate = async () => {
    await generate(reportId)
    setPollEnabled(true)
    void refetch()
  }

  return (
    <Dialog
      title={`Runs — ${reportName}`}
      open
      onClose={onClose}
      width={640}
      footer={
        <div className="flex justify-between">
          {canGenerate && (
            <Button
              variant="primary"
              icon={<RefreshCw size={14} />}
              loading={isGenerating}
              onClick={() => void handleGenerate()}
            >
              Generate now
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={40} />
          ))}
        </div>
      ) : error ? (
        <ErrorState error={error} retry={() => void refetch()} />
      ) : !data || data.items.length === 0 ? (
        <p className="py-6 text-center text-sm text-text-muted">
          No runs yet. Click &ldquo;Generate now&rdquo; to create one.
        </p>
      ) : (
        <div className="divide-y divide-border-subtle">
          {data.items.map((run) => (
            <RunRow key={run.id} run={run} reportId={reportId} />
          ))}
        </div>
      )}
    </Dialog>
  )
}

function RunRow({ run, reportId }: { run: ReportRun; reportId: string }) {
  const isActive = ACTIVE_STATUSES.has(run.status)
  const isCompleted = run.status === 'completed'
  const downloadUrl = `${env.VITE_API_URL}/api/v1/reports/${reportId}/runs/${run.id}/download`

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-sm text-text-primary">{formatTimestamp(run.createdAt)}</p>
        {run.error && <p className="mt-0.5 truncate text-xs text-danger">{run.error}</p>}
        {run.completedAt && (
          <p className="mt-0.5 text-xs text-text-muted">
            Completed {formatTimestamp(run.completedAt)}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <ReportStatusBadge status={run.status} />
        {isActive && (
          <span className="inline-block size-2 animate-spin rounded-full border border-transparent border-t-accent" />
        )}
        {isCompleted && (
          <a href={downloadUrl} download aria-label="Download run" className="contents">
            <Button variant="ghost" size="sm" icon={<Download size={14} />} />
          </a>
        )}
      </div>
    </div>
  )
}
